'use client';
import { useState, useRef, useEffect } from 'react';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftOutlined from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import styles from './DatePicker.module.css';

export interface DatePickerProps {
	value: string;
	onChange: (value: string) => void;
	hasError?: boolean;
	disabled?: boolean;
	placeholder?: string;
	dropdownAlign?: 'left' | 'right';
}

const MONTHS = [
	'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
	'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const DAY_HEADERS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const parseDate = (value: string): Date | null => {
	if (!value) return null;
	const d = new Date(value + 'T00:00:00');
	return isNaN(d.getTime()) ? null : d;
};

const formatDisplay = (value: string): string => {
	const d = parseDate(value);
	if (!d) return '';
	return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const toIso = (year: number, month: number, day: number): string =>
	`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

export const DatePicker = ({
	value,
	onChange,
	hasError,
	disabled,
	placeholder = 'DD/MM/AAAA',
	dropdownAlign = 'left',
}: DatePickerProps) => {
	const today = new Date();
	const selected = parseDate(value);

	const [isOpen, setIsOpen] = useState(false);
	const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? today.getMonth());
	const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? today.getFullYear());

	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		if (isOpen) document.addEventListener('mousedown', handleOutside);
		return () => document.removeEventListener('mousedown', handleOutside);
	}, [isOpen]);

	const open = () => {
		if (disabled) return;
		if (selected) {
			setViewMonth(selected.getMonth());
			setViewYear(selected.getFullYear());
		}
		setIsOpen(o => !o);
	};

	const prevMonth = () => {
		if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
		else setViewMonth(m => m - 1);
	};

	const nextMonth = () => {
		if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
		else setViewMonth(m => m + 1);
	};

	const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
	const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

	const cells: (number | null)[] = [
		...Array<null>(firstDayOfWeek).fill(null),
		...Array.from({ length: daysInMonth }, (_, i) => i + 1),
	];

	const isSelected = (day: number) =>
		selected?.getFullYear() === viewYear &&
		selected?.getMonth() === viewMonth &&
		selected?.getDate() === day;

	const isToday = (day: number) =>
		today.getFullYear() === viewYear &&
		today.getMonth() === viewMonth &&
		today.getDate() === day;

	const handleDayClick = (day: number) => {
		onChange(toIso(viewYear, viewMonth, day));
		setIsOpen(false);
	};

	const display = formatDisplay(value);

	return (
		<div ref={wrapperRef} className={styles.wrapper}>
			<button
				type="button"
				disabled={disabled}
				onClick={open}
				className={`${styles.trigger} ${!display ? styles.empty : ''} ${hasError ? styles.error : ''} ${isOpen ? styles.open : ''}`}
			>
				<span className={styles.triggerText}>{display || placeholder}</span>
				<CalendarMonthOutlined className={styles.icon} />
			</button>

			{isOpen && (
				<div className={`${styles.dropdown} ${dropdownAlign === 'right' ? styles.dropdownRight : ''}`}>
					<div className={styles.header}>
						<button type="button" className={styles.navBtn} onClick={prevMonth}>
							<ChevronLeftOutlined className={styles.navIcon} />
						</button>
						<span className={styles.monthYear}>
							{MONTHS[viewMonth]} {viewYear}
						</span>
						<button type="button" className={styles.navBtn} onClick={nextMonth}>
							<ChevronRightOutlined className={styles.navIcon} />
						</button>
					</div>

					<div className={styles.grid}>
						{DAY_HEADERS.map((d, i) => (
							<div key={`dh-${i}`} className={styles.dayHeader}>{d}</div>
						))}
						{cells.map((day, i) => (
							<div
								key={`dc-${i}`}
								className={[
									styles.cell,
									day === null ? styles.cellEmpty : '',
									day !== null && isSelected(day) ? styles.cellSelected : '',
									day !== null && isToday(day) && !isSelected(day) ? styles.cellToday : '',
								].join(' ')}
								onClick={day !== null ? () => handleDayClick(day) : undefined}
							>
								{day ?? ''}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
